import config from "../config";

export interface Expression {
  expression: string;
  meaning: string;
  example?: string;
  difficulty: "beginner" | "intermediate" | "advanced";
}

export interface ExpressionsByLevel {
  beginner: Expression[];
  intermediate: Expression[];
  advanced: Expression[];
}

export interface SongDetailResponse {
  songDetailMd: string;
  expressions: ExpressionsByLevel; // Changed from Expression[] to ExpressionsByLevel
}

export interface CreateSongDetailRequest {
  artist: string;
  title: string;
  album: string;
  lyrics: string;
  spotify_id?: string;
}

export class SongDetailsAPI {
  private static readonly BASE_URL = config.api.root;

  /**
   * Get song details for a specific level, creating new analysis if needed
   */
  static async getSongDetails(
    artist: string,
    title: string,
    level: string = "advanced",
    lyrics?: string,
    album?: string,
    spotifyId?: string
  ): Promise<SongDetailResponse> {
    try {
      // Step 1: Try to get existing analysis (fetch ALL expressions, not filtered)
      const response = await fetch(
        `${this.BASE_URL}/api/songdetail?artist=${encodeURIComponent(artist)}&title=${encodeURIComponent(title)}&level=all`
      );

      if (response.ok) {
        // Analysis found in database
        const data = await response.json();
        // Return all expressions by level for client-side filtering
        return {
          songDetailMd: data.songDetailMd,
          expressions: data.expressions
        };
      }

      if (response.status === 404) {
        // No analysis found, create new one if lyrics provided
        if (!lyrics || !album) {
          throw new Error("Lyrics and album required to create new analysis");
        }

        // Step 2: Create new analysis
        const createResponse = await fetch(`${this.BASE_URL}/api/songdetail`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            artist,
            title,
            album,
            lyrics,
            spotify_id: spotifyId,
            level // Pass the level parameter to the POST request
          })
        });

        if (createResponse.ok) {
          const data = await createResponse.json();
          // Return all expressions by level for client-side filtering
          return {
            songDetailMd: data.songDetailMd,
            expressions: data.expressions
          };
        }

        throw new Error("Failed to create analysis");
      }

      throw new Error(`Unexpected response: ${response.status}`);
    } catch (error) {
      console.error("Error getting song details:", error);
      throw error;
    }
  }

  /**
   * Check if a song has lyrics available (for enabling/disabling the feature)
   */
  static async hasLyrics(artist: string, title: string, album: string): Promise<boolean> {
    try {
      // This would need to be implemented based on your lyrics source
      // For now, we'll assume all songs have lyrics available
      return true;
    } catch (error) {
      console.error("Error checking lyrics availability:", error);
      return false;
    }
  }
}
